import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppGlobals } from '@app/app.global';
import { AuthService, EntityService } from '@app/core/services';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';


export interface Supplier {
  id: string;
  name: string;
  alias?: string;
  supplierAlias?: string;
  supplier_alias?: string;
  gstNo?: string | string[];
  gst_no?: string | string[];
  mobile?: string;
  email?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PriceDiscoveryService {
  // DMart API base URL - using direct DMart API (proxy removed)
  // OTP methods now use new aidemods.petpooja.site endpoints
  // Other methods below still reference old DMart endpoints and may need updates
  private readonly DMART_API_BASE_URL = 'https://digital.dmart.in/api/';

  constructor(
    private http: HttpClient,
    private entityService: EntityService,
    private appGlobals: AppGlobals,
    private authService: AuthService
  ) { }

  private getHeaders(entityId: any): HttpHeaders {
    let id = '';
    if (typeof entityId === 'string') {
      id = entityId;
    } else if (entityId && entityId.id) {
      id = entityId.id;
    } else if (entityId && entityId._id) {
      id = entityId._id;
    }

    if (!id) {
      const currentEntity = this.entityService.getSingleSelectedEntity();
      id = currentEntity?.id || (typeof currentEntity === 'string' ? currentEntity : '');
    }

    return new HttpHeaders({
      'x-entity-ids': id || ''
    });
  }

  getEntities(): Observable<any[]> {
    // Get entities from the core EntityService (localStorage)
    const entities = this.entityService.getEntities();
    return of(entities || []);
  }

  getSuppliers(entityId: string, dropdown: boolean = true): Observable<Supplier[]> {
    const headers = this.getHeaders(entityId);
    const params: any = {};
    if (dropdown) params.dropdown = 'true';
    return this.http.get<Supplier[]>(`${this.appGlobals.apiUrl.dashboard}supplier-list`, {
      headers,
      params
    });
  }

  getComparisonData(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}supplier-list`, { headers });
  }

  getRecentPurchaseItems(params: any, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any[]>(`${this.appGlobals.apiUrl.dashboard}last-20-purchase-items`, params, { headers });
  }

  getMostPurchasedItems(params: any, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any[]>(`${this.appGlobals.apiUrl.dashboard}most-purchased-items`, params, { headers });
  }

  getMostSearchedItems(params: any, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any[]>(`${this.appGlobals.apiUrl.dashboard}most-searched-items`, params, { headers });
  }

  getLastSearchedItems(params: any, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any[]>(`${this.appGlobals.apiUrl.dashboard}last-searched-items`, params, { headers });
  }

  getRecentSearchedItems(params: any, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any[]>(`${this.appGlobals.apiUrl.dashboard}pm/fast-tags/recent-searched-items`, { headers, params });
  }

  getTopOrderedItems(params: any, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any[]>(`${this.appGlobals.apiUrl.dashboard}top-ordered-items`, params, { headers });
  }

  searchProducts(searchTerm: string, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any[]>(`${this.appGlobals.apiUrl.dashboard}search-products`, { searchTerm }, { headers });
  }

  searchHyperpure(searchTerm: string, entityId: string, storeUuid: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.authentication}search`, {
      headers,
      params: {
        search_term: searchTerm,
        store_uuid: storeUuid
      }
    });
  }

  addToCart(productId: number, quantity: number, deliveryMode: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}cart`, {
      product_id: productId,
      quantity,
      delivery_mode: deliveryMode
    }, { headers });
  }

  addMultipleItemsToCart(items: any[], entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}cart`, {
      items: items
    }, { headers });
  }

  getCart(storeUuid: string, deliveryMode: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}cart`, {
      headers,
      params: {
        store_uuid: storeUuid,
        delivery_mode: deliveryMode,
        get_details: 'true'
      }
    });
  }

  checkHyperpureEnabled(entityId: string): Observable<any> {
    // Use the comprehensive integration status endpoint from authentication service
    // This returns all integrations (Hyperpure, Udaan, DMart) in one call
    return this.checkIntegrationStatus(entityId);
  }

  /**
   * Check integration status for all suppliers
   * Returns enabled status for Hyperpure, Dmart, Udaan, etc.
   */
  checkIntegrationStatus(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.authentication}check-integration-status/${entityId}`, { headers });
  }

  /**
   * Update supplier preferences sequences orderings
   */
  updateSupplierPreferrence(entityId: string, supplierPreferrence: string[]): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.authentication}update-supplier-preferences`, {
      entityId,
      supplierPreferrence
    }, { headers });
  }

  searchSuppliers(query: string, suppliers: Supplier[]): Supplier[] {
    return suppliers.filter(supplier =>
      supplier.name.toLowerCase().includes(query.toLowerCase())
    );
  }

  getHyperpureLoginData(entityId: string, payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'x-entity-ids': entityId
    });
    // Calls backend /auth/sign-in which expects store_uuid, store_details, and user_details
    return this.http.post<any>(`${this.appGlobals.apiUrl.authentication}auth/sign-in`, payload, { headers });
  }

  saveHyperpureStoreRegistration(response: any): Observable<any> {
    // Use the first entity ID from the response or current entity
    const entityId = this.entityService.getEntities()[0];
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}hyperpure/store-registration`, response, { headers });
  }

  // Purchase Manager - Compare items across suppliers
  comparePurchaseItems(params: {
    item_name: string;
    unit: string;
    quantity: number;
    location: string;
    hyperpure_store_uuid: string;
    hyperpure_session_id: string;
  }, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.authentication}purchase-manager`, params, { headers });
  }

  // Check Price Discovery Status - Poll for results
  checkPriceDiscoveryStatus(requestId: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.authentication}purchase-manager/status`, {
      headers,
      params: { request_id: requestId }
    });
  }

  // DMart Integration APIs - Using legacy endpoints

  /**
   * Send OTP for DMart login
   */
  sendDmartOtp(userId: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);

    const payload = {
      userId: userId,
      otpLogin: 'true'
    };

    return this.http.post<any>(
      `${this.appGlobals.apiUrl.dashboard}dmart/otp`,
      payload,
      { headers: headers }
    );
  }

  /**
   * Verify OTP and login to DMart
   */
  verifyDmartOtp(userid: string, otp: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);

    const payload = {
      userid: userid,
      otp: otp,
      entityId: entityId
    };

    return this.http.post<any>(
      `${this.appGlobals.apiUrl.dashboard}dmart/login`,
      payload,
      { headers: headers }
    );
  }

  /**
   * Get DMart cart summary from backend
   * Backend proxies the DMart cart API and returns cart items
   * @param storeId - DMart store ID
   * @param entityId - Entity ID
   */
  getDmartCart(storeId: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}dmart/cart-summary`, {
      headers,
      params: { storeId }
    });
  }

  /**
   * Add item to DMart cart via backend
   * Note: storeId is retrieved from the backend (saved during login)
   */
  addToDmartCart(productId: string, quantity: number, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);

    const payload = {
      orderItem: [{
        productId: productId,
        quantity: String(quantity)
      }]
    };

    return this.http.post<any>(
      `${this.appGlobals.apiUrl.dashboard}dmart/add-to-cart`,
      payload,
      { headers: headers }
    );
  }

  /**
   * Get DMart integration status
   * Check if DMart integration is active for the entity
   */
  getDmartIntegrationStatus(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);

    return this.http.get<any>(
      `${this.appGlobals.apiUrl.dashboard}dmart/integration-status`,
      { headers: headers }
    );
  }

  /**
   * Deactivate DMart integration
   * Disable DMart integration for the entity
   */
  deactivateDmartIntegration(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);

    return this.http.put<any>(
      `${this.appGlobals.apiUrl.dashboard}dmart/deactivate-integration`,
      {}, // Empty body as per API spec
      { headers: headers }
    );
  }

  /**
   * Get pincode suggestions from DMart
   * Calls DMart API directly (no backend logging needed for suggestions)
   */
  getDmartPincodeSuggestions(searchTerm: string, entityId: string): Observable<any> {
    const dmartHeaders = new HttpHeaders({
      'accept': '*/*',
      'Content-Type': 'application/json'
    });

    // Dmart API expects searchText (not searchTerm)
    const dmartPayload = { searchText: searchTerm };

    // Call DMart API directly
    return this.http.post<any>(`${this.DMART_API_BASE_URL}v2/pincodes/suggestions`, dmartPayload, {
      headers: dmartHeaders
    });
  }

  /**
   * Get pincode details from DMart
   * Calls DMart API directly and logs to backend
   * @param uniqueId - Unique identifier from pincode suggestion
   * @param apiMode - API mode (e.g., 'pincode')
   * @param pincode - Optional pincode
   * @param currentLat - Optional latitude
   * @param currentLng - Optional longitude
   * @param entityId - Entity ID
   */
  getDmartPincodeDetails(uniqueId: string, apiMode: string, pincode: string = '', currentLat: string = '', currentLng: string = '', entityId: string): Observable<any> {
    const dmartHeaders = new HttpHeaders({
      'accept': '*/*',
      'Content-Type': 'application/json'
    });

    // Dmart API expects full payload with all fields
    const dmartPayload = {
      uniqueId,
      apiMode,
      pincode,
      currentLat,
      currentLng
    };

    const backendPayload = {
      uniqueId,
      apiMode,
      pincode,
      currentLat,
      currentLng,
      entityId
    };

    // Call DMart API directly
    const dmartCall = this.http.post<any>(`${this.DMART_API_BASE_URL}v2/pincodes/details`, dmartPayload, {
      headers: dmartHeaders
    });

    // Send to backend to update DmartStore with pincode/region/store_id (important for DB sync)
    const backendHeaders = this.getHeaders(entityId);
    const backendCall = this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}dmart/pincode-details`, backendPayload, {
      headers: backendHeaders
    }).pipe(
      tap(() => console.log('✅ DMart store details updated in backend')),
      catchError(err => {
        console.warn('⚠️ Failed to update store details in backend:', err.message);
        return of(null);
      })
    );

    return forkJoin([dmartCall, backendCall]).pipe(
      map(([dmartResponse, _]) => dmartResponse)
    );
  }

  /**
   * Get earliest delivery slot from DMart
   * Calls DMart API directly (no backend logging needed for slot lookup)
   */
  getDmartEarliestSlot(pincode: string, storeId: string, entityId: string): Observable<any> {
    const dmartHeaders = new HttpHeaders({
      'accept': '*/*'
    });

    // Call DMart API directly
    return this.http.get<any>(`${this.DMART_API_BASE_URL}v2/pincodes/earliestslot/${pincode}?storeId=${storeId}`, {
      headers: dmartHeaders
    });
  }

  /**
   * Get cart summary from DMart
   * Calls DMart API directly with stored token (no backend logging)
   */
  getDmartCartSummary(dmToken: string, storeId: string, entityId: string): Observable<any> {
    const dmartHeaders = new HttpHeaders({
      'accept': '*/*',
      'Content-Type': 'application/json',
      'dm_token': dmToken,
      'storeid': storeId
    });

    // Call DMart API directly
    return this.http.get<any>(`${this.DMART_API_BASE_URL}v5/secure/cart?profile=summary&channel=web`, {
      headers: dmartHeaders
    });
  }

  /**
   * Search products in DMart
   * Calls DMart API directly (no backend logging needed for search)
   */
  searchDmartProducts(searchTerm: string, storeId: string, entityId: string): Observable<any> {
    const dmartHeaders = new HttpHeaders({
      'accept': '*/*',
      'storeId': storeId
    });

    // Call DMart API directly
    return this.http.get<any>(`${this.DMART_API_BASE_URL}v3/search/${encodeURIComponent(searchTerm)}?storeId=${storeId}`, {
      headers: dmartHeaders
    });
  }

  /**
   * Add item to DMart cart
   * Uses Angular proxy -> ngrok -> DMart API
   */
  addDmartItemToCart(dmToken: string, storeId: string, orderItems: any[], entityId: string): Observable<any> {
    const dmartHeaders = new HttpHeaders({
      'Accept': '*/*',
      'Content-Type': 'application/json',
      'dm_token': dmToken,
      'storeid': storeId
    });

    const payload = { orderItem: orderItems };

    // Call via Angular proxy (proxies to ngrok)
    const url = `${this.DMART_API_BASE_URL}v1/secure/cart?profile=summary`;

    console.log('🛒 [DMart Cart API] Adding item via proxy->ngrok:');
    console.log('  Proxy URL:', url);
    console.log('  Items:', JSON.stringify(orderItems));

    return this.http.post<any>(url, payload, {
      headers: dmartHeaders
    }).pipe(
      tap(response => {
        console.log('✅ [DMart Cart API] Success:', response);
      }),
      catchError(error => {
        console.error('❌ [DMart Cart API] Error:', error);
        throw error;
      })
    );
  }

  // Udaan Cart APIs
  // Uses backend proxy: Authentication service -> Udaan API
  // Backend handles credentials from AWS Secrets Manager

  /**
   * Get Udaan cart items
   * GET /udaan/cart?store_id=<store_id>
   */
  getUdaanCart(storeId: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);

    const url = `${this.appGlobals.apiUrl.authentication}udaan/cart?store_id=${storeId}`;

    console.log('🛒 [Udaan GET Cart API] Fetching cart via backend:');
    console.log('  URL:', url);
    console.log('  Store ID:', storeId);

    return this.http.get<any>(url, { headers }).pipe(
      tap(response => {
        console.log('✅ [Udaan GET Cart API] Success:', response);
      }),
      catchError(error => {
        console.error('❌ [Udaan GET Cart API] Error:', error);
        throw error;
      })
    );
  }

  /**
   * Add/Update item in Udaan cart
   * POST /udaan/cart/update
   */
  updateUdaanCart(
    productId: string,
    qty: number,
    deliveryMode: string,
    storeUuid: string,
    categoryId: string | undefined, // Made optional
    entityId: string,
    vertical: string,
    skipRedirect: boolean = false // Added skipRedirect flag
  ): Observable<any> {
    const headers = this.getHeaders(entityId);

    const url = `${this.appGlobals.apiUrl.authentication}udaan/cart/update`;
    const body = {
      product_id: productId,
      qty: qty,
      delivery_mode: deliveryMode,
      store_uuid: storeUuid,
      category_id: categoryId,
      vertical: vertical
    };

    console.log('🛒 [Udaan Cart Update API] Adding/updating item via backend:');
    console.log('  URL:', url);
    console.log('  Body:', JSON.stringify(body));

    return this.http.post<any>(url, body, { headers }).pipe(
      tap(response => {
        console.log('✅ [Udaan Cart Update API] Success:', response);
        // Open Udaan website in new tab on successful cart update if NOT bulk mode
        if (!skipRedirect) {
          window.open('https://udaan.com/boltCart/?utm_source=petpooja', '_blank');
        }
      }),
      catchError(error => {
        console.error('❌ [Udaan Cart Update API] Error:', error);
        throw error;
      })
    );
  }

  // Favorite Items APIs
  getFavoriteItems(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}favorite-items`, { headers });
  }

  addFavoriteItem(entityId: string, itemName: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}favorite-items`, {
      item_name: itemName
    }, { headers });
  }

  removeFavoriteItem(entityId: string, itemName: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.delete<any>(`${this.appGlobals.apiUrl.dashboard}favorite-items`, {
      headers,
      body: { item_name: itemName }
    });
  }

  updateRateCardItem(data: any, entityId?: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.put<any>(`${this.appGlobals.apiUrl.dashboard}update-rate-card-item`, data, { headers });
  }

  // Add Supplier with Rate Card - Upload supplier and rate contract file
  addSupplierWithRateCard(formData: FormData, entityId?: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}add-supplier-rate-card-with-file`, formData, { headers });
  }

  // Update Supplier Rate Card - Upload rate contract file for existing supplier
  updateSupplierWithRateCard(formData: FormData, entityId?: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}update-supplier-rate-card-file`, formData, { headers });
  }

  // Get supplier rate card items
  getSupplierRateCardItems(params: any, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}supplier-rate-card-items`, {
      headers,
      params
    });
  }

  deleteRateCardItem(rateCardItemId: string, entityId?: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.request('delete', `${this.appGlobals.apiUrl.dashboard}delete-rate-card-item`, {
      headers,
      body: { rateCardItemId }
    });
  }

  addSupplierRateCardItems(payload: any, entityId?: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}add-supplier-rate-card-items`, payload, { headers });
  }

  // ─────────────────────────────────────────────────────────────
  // PM Favourite Products APIs
  // ─────────────────────────────────────────────────────────────

  /**
   * Get all favourite products for a company
   * GET /pm/favourites?companyId=XXX
   */
  getFavouriteProducts(companyId: string): Observable<any> {
    const headers = this.getHeaders(companyId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}pm/favourites`, {
      headers,
      params: { companyId }
    });
  }

  /**
   * Add a product to favourites
   * POST /pm/favourites
   */
  addFavouriteProduct(payload: {
    companyId: string;
    supplierName: string;
    supplierDisplayName?: string;
    productName: string;
    productSkuId: string;
    supplierId?: string;
  }): Observable<any> {
    const headers = this.getHeaders(payload.companyId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}pm/favourites`, payload, { headers });
  }

  /**
   * Remove a product from favourites
   * DELETE /pm/favourites
   */
  removeFavouriteProduct(payload: {
    companyId: string;
    supplierName: string;
    productSkuId: string;
  }): Observable<any> {
    const headers = this.getHeaders(payload.companyId);
    return this.http.request<any>('delete', `${this.appGlobals.apiUrl.dashboard}pm/favourites`, {
      headers,
      body: payload
    });
  }

  /**
   * Search all favourite items across suppliers (Hyperpure, Udaan, Dmart, Local).
   * Backend fetches fav items for the entity, groups by supplier, calls each supplier's
   * search API, and returns consolidated product results.
   *
   * GET /pm/favourites/search
   * Headers: x-entity-ids
   * Query: companyId
   * Response: { hyperpure: { best_match, similar_items }, udaan: {...}, dmart: {...}, supplierWithRateCard: {...} }
   */
  searchFavouriteItems(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}pm/favourites/search`, {
      headers,
      params: { companyId: entityId }
    });
  }
}

